import React from 'react';
import { CircleCheck, Mail } from '@saleshandy/icons';
import { Field } from './types';
import TruncatedTextWithTooltip from '../../../../components/truncated-text-with-tooltip';
import { getFormattedTime } from '../../../../utils';

/**
 * Check if field is a custom attribute (not a system attribute)
 */
export const isCustomAttribute = (
  field: Field,
  fields: any[],
  columnGroups: any[],
): boolean => {
  // Check if field is in Custom Attributes group
  const customAttributesGroup = columnGroups.find(
    (group) =>
      group.id === 'CustomAttributes' || group.title === 'Custom Attributes',
  );
  if (customAttributesGroup) {
    const isInCustomGroup = customAttributesGroup.fields.some(
      (f) => f?.id?.toString() === field?.id?.toString(),
    );
    if (isInCustomGroup) {
      return true;
    }
  }

  // Fallback: Check if field exists in fields array and is not a default/system field
  const fieldFromFields = fields?.find(
    (f) => f?.id?.toString() === field?.id?.toString(),
  );
  return fieldFromFields ? !fieldFromFields.isDefault : false;
};

/**
 * Get mock value based on fieldType for custom attributes
 */
export const getMockValueByFieldType = (fieldType: string): string => {
  switch (fieldType?.toLowerCase()) {
    case 'text':
      return 'John Doe';
    case 'number':
      return '1000';
    case 'long-text':
    case 'longtext':
      return "Hi there, I hope you're doing well! I wanted to reached out with something that might be valuable for you.";
    case 'date':
      return '01/01/2025';
    case 'dropdown':
      return 'Interested';
    case 'currency':
      return '$100';
    default:
      return 'John Doe';
  }
};

/**
 * Truncate long text with ellipsis
 */
export const truncateText = (text: string, maxLength = 30): string => {
  if (!text || text.length <= maxLength) {
    return text;
  }
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Mock prospect data for card preview
 * This represents sample data that matches the field labels
 */
export const getMockProspectData = () => {
  // Create a mock prospect object based on the provided sample data
  const mockData: any = {
    firstName: 'Michelle',
    lastName: 'Young',
    email: 'jenkinsjake@hull.com',
    jobTitle: 'DevOps Associate',
    emailServiceProvider: 'Zoho',
    tags: [],
    emailVerified: 'No',
    department: 'Associate',
    company: 'Arnold Ltd',
    companyIndustry: 'AI Research',
    companyWebsite: 'https://www.saleshandy.com/',
    prospectOwner: 'David Adams',
    verificationSubStatus: 'Valid',
    freeAccount: 'Yes',
    mxRecord: 'Missing',
    creationSource: 'API',
    clientAssociated: 'Radox',
    linkedIn: 'https://www.linkedin.com/company/saleshandy/',
    experience: '10 years',
    phoneNumber: '(917)316-3325-400',
    twitter: 'https://x.com/saleshandy',
    facebook: 'https://www.facebook.com/saleshandy/',
    city: 'Jacobport',
    state: 'Illinois',
    country: 'Comoros',
    companyPhone: '(868)501-4294-019',
    companyDomain: 'saleshandy.com',
    companySize: '11-50',
    companyRevenue: '$491M',
    companyFounder: 'Kayla Cruz',
    sequenceAssociated: 'New Sequence - Fri 12th Dec',
    subsequence: 'Sub A: New Sequence - Fri 12th Dec',
    latestStatus: 'Active',
    prospectTags: [{ id: 1, name: 'New tag' }],
    emailVerificationStatus: 'Valid',
    lastContacted: '2025-12-15T23:27:38Z',
    industry: 'Software Development',
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
    lastUpdatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
    freeEmailAccount: 'Yes',
    numberOfEmailsSent: '2',
    companyLinkedIn: 'https://www.linkedin.com/quad-manufacturing',
    companyFoundedYear: '2005',
    numberOfOpens: '2',
    numberOfClicks: '3',
    recentlyOpenDate: '2025-12-15T23:27:38Z',
    recentlyClickDate: '2025-12-15T23:27:38Z',
    recentlyReplyDate: '2025-12-15T23:27:38Z',
    dealValue: '$20',
    closingDate: '2025-12-10',
    createdBy: 'David Obrien',
    engagementFields: {
      numberOfEmailsSent: '2',
      numberOfOpens: '2',
      numberOfClicks: '3',
      recentlyOpenDate: '2025-12-15T23:27:38Z',
      recentlyClickDate: '2025-12-15T23:27:38Z',
      recentlyReplyDate: '2025-12-15T23:27:38Z',
      lastContacted: '2025-12-15T23:27:38Z',
    },
    // Map field labels to values for attributes
    attributes: [
      { fieldRef: { label: 'First Name' }, attributeValue: 'Michelle' },
      { fieldRef: { label: 'Last Name' }, attributeValue: 'Young' },
      {
        fieldRef: { label: 'Email Address' },
        attributeValue: 'jenkinsjake@hull.com',
      },
      {
        fieldRef: { label: 'Job Title' },
        attributeValue: 'DevOps Associate',
      },
      { fieldRef: { label: 'Prospect ESP' }, attributeValue: 'Zoho' },
      { fieldRef: { label: 'Tags' }, attributeValue: 'Others' },
      { fieldRef: { label: 'Email Verified' }, attributeValue: 'No' },
      { fieldRef: { label: 'Department' }, attributeValue: 'Associate' },
      { fieldRef: { label: 'Company' }, attributeValue: 'Arnold Ltd' },
      {
        fieldRef: { label: 'Company Industry' },
        attributeValue: 'AI Research',
      },
      {
        fieldRef: { label: 'Industry' },
        attributeValue: 'Software Development',
      },
      {
        fieldRef: { label: 'Company Website' },
        attributeValue: 'https://www.saleshandy.com/',
      },
      {
        fieldRef: { label: 'Prospect Owner' },
        attributeValue: 'David Adams',
      },
      {
        fieldRef: { label: 'Email Verification Sub Status' },
        attributeValue: 'Valid',
      },
      {
        fieldRef: { label: 'Email Verification Status' },
        attributeValue: 'Valid',
      },
      { fieldRef: { label: 'Free Account' }, attributeValue: 'Yes' },
      { fieldRef: { label: 'Free Email Account' }, attributeValue: 'Yes' },
      { fieldRef: { label: 'MX Record' }, attributeValue: 'Missing' },
      { fieldRef: { label: 'Creation Source' }, attributeValue: 'API' },
      { fieldRef: { label: 'Client Associated' }, attributeValue: 'Radox' },
      {
        fieldRef: { label: 'LinkedIn' },
        attributeValue: 'https://www.linkedin.com/company/saleshandy/',
      },
      {
        fieldRef: { label: 'Company LinkedIn' },
        attributeValue: 'https://www.linkedin.com/quad-manufacturing',
      },
      { fieldRef: { label: 'Experience' }, attributeValue: '10 years' },
      {
        fieldRef: { label: 'Phone Number' },
        attributeValue: '(917)316-3325-400',
      },
      {
        fieldRef: { label: 'Twitter' },
        attributeValue: 'https://x.com/saleshandy',
      },
      {
        fieldRef: { label: 'Facebook' },
        attributeValue: 'https://www.facebook.com/saleshandy/',
      },
      { fieldRef: { label: 'City' }, attributeValue: 'Jacobport' },
      { fieldRef: { label: 'State' }, attributeValue: 'Illinois' },
      { fieldRef: { label: 'Country' }, attributeValue: 'Comoros' },
      {
        fieldRef: { label: 'Company Phone' },
        attributeValue: '(868)501-4294-019',
      },
      {
        fieldRef: { label: 'Company Domain' },
        attributeValue: 'saleshandy.com',
      },
      { fieldRef: { label: 'Company Size' }, attributeValue: '11-50' },
      { fieldRef: { label: 'Company Revenue' }, attributeValue: '$491M' },
      {
        fieldRef: { label: 'Company Founder' },
        attributeValue: 'Kayla Cruz',
      },
      { fieldRef: { label: 'Company Founded Year' }, attributeValue: '2005' },
      {
        fieldRef: { label: 'Sequence Associated' },
        attributeValue: 'New Sequence - Fri 12th Dec',
      },
      {
        fieldRef: { label: 'Subsequence' },
        attributeValue: 'Sub A: New Sequence - Fri 12th Dec',
      },
      { fieldRef: { label: 'Latest Status' }, attributeValue: 'Active' },
      { fieldRef: { label: 'Prospect Tag' }, attributeValue: 'New tag' },
      {
        fieldRef: { label: 'Last Contacted' },
        attributeValue: '2025-12-15T23:27:38Z',
      },
      {
        fieldRef: { label: 'Creation Date' },
        attributeValue: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      },
      {
        fieldRef: { label: 'Last Updated' },
        attributeValue: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      },
      { fieldRef: { label: 'Number of Email Sent' }, attributeValue: '2' },
      { fieldRef: { label: 'Number of Opens' }, attributeValue: '2' },
      { fieldRef: { label: 'Number of clicks' }, attributeValue: '3' },
      {
        fieldRef: { label: 'Recently open date' },
        attributeValue: '2025-12-15T23:27:38Z',
      },
      {
        fieldRef: { label: 'Recently click date' },
        attributeValue: '2025-12-15T23:27:38Z',
      },
      {
        fieldRef: { label: 'Recently reply date' },
        attributeValue: '2025-12-15T23:27:38Z',
      },
      { fieldRef: { label: 'Deal Value' }, attributeValue: '$20' },
      { fieldRef: { label: 'Closing Date' }, attributeValue: '2025-12-10' },
      { fieldRef: { label: 'Created By' }, attributeValue: 'David Obrien' },
    ],
  };
  return mockData;
};

/**
 * Get field value for preview based on field label and id
 */
export const getPreviewFieldValue = (
  field: Field,
  mockProspectData: any,
  fields: any[],
  columnGroups: any[],
): string | React.ReactNode | null => {
  const fieldId = field?.id?.toString() || '';
  const fieldLabel = field?.label?.trim() || '';
  const labelLower = fieldLabel.toLowerCase();

  // Check if this is a custom attribute
  const isCustom = isCustomAttribute(field, fields, columnGroups);
  if (isCustom) {
    // Get fieldType from fields array
    const fieldFromFields = fields?.find((f) => f?.id?.toString() === fieldId);
    const fieldType = fieldFromFields?.fieldType || field?.fieldType || 'text';
    const mockValue = getMockValueByFieldType(fieldType);

    // Handle long-text with truncation
    if (
      fieldType?.toLowerCase() === 'long-text' ||
      fieldType?.toLowerCase() === 'longtext'
    ) {
      return <TruncatedTextWithTooltip string={mockValue} length={38} />;
    }

    return mockValue;
  }

  let value: any = null;

  // First, try to find by label in attributes (most reliable for custom fields)
  if (mockProspectData?.attributes) {
    const attribute = mockProspectData.attributes.find(
      (attr: any) =>
        attr?.fieldRef?.label?.trim()?.toLowerCase() === labelLower,
    );
    if (
      attribute?.attributeValue !== null &&
      attribute?.attributeValue !== undefined &&
      attribute?.attributeValue !== ''
    ) {
      value = attribute.attributeValue;
    }
  }

  // If not found in attributes, try direct property access on mockProspectData
  if (!value && fieldId) {
    // Try direct property access
    if (
      mockProspectData[fieldId] !== undefined &&
      mockProspectData[fieldId] !== null
    ) {
      value = mockProspectData[fieldId];
    }
    // Try camelCase conversion
    const camelCaseId = fieldId.charAt(0).toLowerCase() + fieldId.slice(1);
    if (
      !value &&
      mockProspectData[camelCaseId] !== undefined &&
      mockProspectData[camelCaseId] !== null
    ) {
      value = mockProspectData[camelCaseId];
    }
  }

  // Check engagementFields for engagement-related fields
  if (!value && mockProspectData?.engagementFields) {
    const engagementFieldKey = labelLower.replace(/\s+/g, '');
    if (mockProspectData.engagementFields[engagementFieldKey] !== undefined) {
      value = mockProspectData.engagementFields[engagementFieldKey];
    }
  }

  // Handle special cases with custom rendering
  if (labelLower === 'email' || labelLower === 'email address') {
    const emailValue = value || mockProspectData.email || '';
    if (emailValue) {
      return (
        <div className="d-flex align-items-center gap-4">
          <span>{emailValue}</span>
          <CircleCheck width={16} height={16} color="#047857" />
        </div>
      );
    }
    return null;
  }

  // Handle Prospect Outcome
  if (labelLower === 'prospect outcomes') {
    return (
      <div className="prospect-outcome-card">
        <Mail width={14} height={14} color="#6b7280" />
        <span className="outcome-name">Uncategorized</span>
      </div>
    );
  }

  // Handle Sequence Associated
  if (labelLower === 'sequence associated') {
    return (
      <span className="sequence-name">
        {value || mockProspectData.sequenceAssociated || ''}
      </span>
    );
  }

  // Handle Subsequence
  if (labelLower === 'subsequence') {
    return value || mockProspectData.subsequence || '';
  }

  // Handle Latest Status
  if (labelLower === 'latest status') {
    const statusValue = value || mockProspectData.latestStatus || '';
    if (statusValue) {
      return <span className="latest-status-badge">{statusValue}</span>;
    }
    return '';
  }

  // Handle Prospect Tag/Tags
  if (
    labelLower === 'prospect tag' ||
    labelLower === 'prospect tags' ||
    labelLower === 'tags'
  ) {
    if (
      mockProspectData.prospectTags &&
      Array.isArray(mockProspectData.prospectTags) &&
      mockProspectData.prospectTags.length > 0
    ) {
      return (
        <>
          {mockProspectData.prospectTags.map((tag: any, index: number) => (
            <span key={index} className="prospect-tag-badge">
              {tag.name || tag}
            </span>
          ))}
        </>
      );
    }
    return <span className="prospect-tag-badge">{value || 'New tag'}</span>;
  }

  // Handle Email Verification Status
  if (labelLower === 'email verification status') {
    const statusValue = value || mockProspectData.emailVerificationStatus || '';
    if (statusValue) {
      return (
        <div className="d-flex align-items-center gap-4">
          <CircleCheck width={16} height={16} color="#047857" />
          <span>{statusValue}</span>
        </div>
      );
    }
    return null;
  }

  // Handle Last Contacted
  if (labelLower === 'last contacted') {
    const dateValue =
      value ||
      mockProspectData.lastContacted ||
      mockProspectData.engagementFields?.lastContacted ||
      '';
    if (dateValue) {
      try {
        const date = new Date(dateValue);
        // Format as YYYY-MM-DD HH:mm:ss
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      } catch {
        return dateValue;
      }
    }
    return '';
  }

  // Handle Industry
  if (labelLower === 'industry') {
    return value || mockProspectData.industry || '';
  }

  // Handle Creation Date
  if (labelLower === 'creation date') {
    const dateValue = value || mockProspectData.createdAt || '';
    if (dateValue) {
      try {
        return getFormattedTime(dateValue);
      } catch {
        return dateValue;
      }
    }
    return '';
  }

  // Handle Last Updated
  if (labelLower === 'last updated') {
    const dateValue = value || mockProspectData.lastUpdatedAt || '';
    if (dateValue) {
      try {
        return getFormattedTime(dateValue);
      } catch {
        return dateValue;
      }
    }
    return '';
  }

  // Handle Free Email Account
  if (labelLower === 'free email account') {
    return value || mockProspectData.freeEmailAccount || '';
  }

  // Handle Number of Email Sent
  if (
    labelLower === 'number of email sent' ||
    labelLower === 'number of emails sent'
  ) {
    return (
      value ||
      mockProspectData.numberOfEmailsSent ||
      mockProspectData.engagementFields?.numberOfEmailsSent ||
      ''
    );
  }

  // Handle Company LinkedIn
  if (labelLower === 'company linkedin' || labelLower === 'company linkedin') {
    return value || mockProspectData.companyLinkedIn || '';
  }

  // Handle Company Founded Year
  if (labelLower === 'company founded year') {
    return value || mockProspectData.companyFoundedYear || '';
  }

  // Handle Number of Opens
  if (labelLower === 'number of opens') {
    return (
      value ||
      mockProspectData.numberOfOpens ||
      mockProspectData.engagementFields?.numberOfOpens ||
      ''
    );
  }

  // Handle Number of clicks
  if (labelLower === 'number of clicks' || labelLower === 'number of clicks') {
    return (
      value ||
      mockProspectData.numberOfClicks ||
      mockProspectData.engagementFields?.numberOfClicks ||
      ''
    );
  }

  // Handle Recently open date
  if (
    labelLower === 'recently open date' ||
    labelLower === 'recently opened date'
  ) {
    const dateValue =
      value ||
      mockProspectData.recentlyOpenDate ||
      mockProspectData.engagementFields?.recentlyOpenDate ||
      '';
    if (dateValue) {
      try {
        const date = new Date(dateValue);
        // Format as YYYY-MM-DD HH:mm:ss
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      } catch {
        return dateValue;
      }
    }
    return '';
  }

  // Handle Recently click date
  if (
    labelLower === 'recently click date' ||
    labelLower === 'recently clicked date'
  ) {
    const dateValue =
      value ||
      mockProspectData.recentlyClickDate ||
      mockProspectData.engagementFields?.recentlyClickDate ||
      '';
    if (dateValue) {
      try {
        const date = new Date(dateValue);
        // Format as YYYY-MM-DD HH:mm:ss
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      } catch {
        return dateValue;
      }
    }
    return '';
  }

  // Handle Recently reply date
  if (
    labelLower === 'recently reply date' ||
    labelLower === 'recently replied date'
  ) {
    const dateValue =
      value ||
      mockProspectData.recentlyReplyDate ||
      mockProspectData.engagementFields?.recentlyReplyDate ||
      '';
    if (dateValue) {
      try {
        const date = new Date(dateValue);
        // Format as YYYY-MM-DD HH:mm:ss
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      } catch {
        return dateValue;
      }
    }
    return '';
  }

  // Handle Deal Value
  if (labelLower === 'deal value') {
    return value || mockProspectData.dealValue || '';
  }

  // Handle Closing Date
  if (labelLower === 'closing date') {
    const dateValue = value || mockProspectData.closingDate || '';
    if (dateValue) {
      try {
        const date = new Date(dateValue);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
        });
      } catch {
        return dateValue;
      }
    }
    return '';
  }

  // Handle Created By
  if (labelLower === 'created by') {
    return value || mockProspectData.createdBy || '';
  }

  // Handle name field - combine first and last name
  if (labelLower === 'name' || labelLower === 'prospect name') {
    const firstName =
      value ||
      mockProspectData.firstName ||
      mockProspectData.attributes?.find(
        (attr: any) =>
          attr?.fieldRef?.label?.trim()?.toLowerCase() === 'first name',
      )?.attributeValue ||
      '';
    const lastName =
      mockProspectData.lastName ||
      mockProspectData.attributes?.find(
        (attr: any) =>
          attr?.fieldRef?.label?.trim()?.toLowerCase() === 'last name',
      )?.attributeValue ||
      '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || '(No Name)';
  }

  // Handle first name and last name separately
  if (labelLower === 'first name') {
    return (
      value ||
      mockProspectData.firstName ||
      mockProspectData.attributes?.find(
        (attr: any) =>
          attr?.fieldRef?.label?.trim()?.toLowerCase() === 'first name',
      )?.attributeValue ||
      ''
    );
  }

  if (labelLower === 'last name') {
    return (
      value ||
      mockProspectData.lastName ||
      mockProspectData.attributes?.find(
        (attr: any) =>
          attr?.fieldRef?.label?.trim()?.toLowerCase() === 'last name',
      )?.attributeValue ||
      ''
    );
  }

  // Handle company field
  if (labelLower === 'company') {
    return (
      value ||
      mockProspectData.company ||
      mockProspectData.attributes?.find(
        (attr: any) =>
          attr?.fieldRef?.label?.trim()?.toLowerCase() === 'company',
      )?.attributeValue ||
      ''
    );
  }

  // Return the value or empty string
  return value || '';
};
export const getFilterIdFromLabel = (
  id: string,
  fields: any[],
): string | null => {
  // Try to find a matching field by label
  const field = fields?.find(
    (f) => f?.label?.trim()?.toLowerCase() === id?.trim()?.toLowerCase(),
  );

  const filterEmail = fields?.find((f) => f?.id === id?.trim()?.toLowerCase())
    ?.label;
  if (filterEmail === 'Email') {
    return 'email';
  }
  if (field) {
    if (field?.label === 'Email') {
      return 'email';
    }
    // Return mapped ID if a match is found
    return field?.id?.toString() ?? null;
  }
  // If no match found, return the id itself
  return id;
};

export const getDisplayField = (field: Field) =>
  field?.label?.toLowerCase() === 'company'
    ? { ...field, label: 'Company Name' }
    : field;
